const desktopZoomQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

class OtkProductMediaViewer {
  constructor(gallery) {
    this.gallery = gallery;
    this.slideshowContainer = gallery.querySelector('slideshow-container');
    this.zoomDialog = gallery.querySelector('zoom-dialog');
    this.dialog = this.zoomDialog?.querySelector('dialog');
    this.abortController = new AbortController();
    this.pointer = null;
    this.animationFrame = null;
    this.opener = null;
  }

  init() {
    if (!this.slideshowContainer) return;

    this.gallery.classList.add('otk-product-gallery');
    this.createHoverZoom();
    this.createViewerControls();

    const { signal } = this.abortController;
    this.slideshowContainer.addEventListener('pointerenter', this.handlePointerEnter, { signal });
    this.slideshowContainer.addEventListener('pointermove', this.handlePointerMove, { signal });
    this.slideshowContainer.addEventListener('pointerleave', this.hideHoverZoom, { signal });
    this.gallery.addEventListener('pointerdown', this.captureOpener, { signal, capture: true });
    desktopZoomQuery.addEventListener('change', this.hideHoverZoom, { signal });

    this.gallery.dataset.otkProductMediaViewerInitialized = 'true';
  }

  createHoverZoom() {
    this.hoverPane = document.createElement('div');
    this.hoverPane.className = 'otk-product-hover-zoom';
    this.hoverPane.setAttribute('aria-hidden', 'true');
    this.hoverPane.hidden = true;

    this.hoverLens = document.createElement('div');
    this.hoverLens.className = 'otk-product-hover-lens';
    this.hoverLens.setAttribute('aria-hidden', 'true');
    this.hoverLens.hidden = true;

    this.gallery.append(this.hoverPane, this.hoverLens);
  }

  createViewerControls() {
    if (!this.dialog || !this.zoomDialog) return;

    this.dialog.classList.add('otk-product-media-viewer-dialog');

    let sidebar = this.dialog.querySelector('[data-otk-product-viewer-sidebar]');
    if (!sidebar) {
      sidebar = document.createElement('aside');
      sidebar.className = 'otk-product-image-viewer__sidebar';
      sidebar.dataset.otkProductViewerSidebar = 'true';

      const productTitle = this.gallery.closest('.shopify-section')?.querySelector('.product-details h1')?.textContent?.trim();
      const title = document.createElement('p');
      title.className = 'otk-product-image-viewer__title';
      title.textContent = productTitle || 'Product images';

      this.viewerCounter = document.createElement('p');
      this.viewerCounter.className = 'otk-product-image-viewer__counter';
      this.viewerCounter.setAttribute('aria-live', 'polite');

      const thumbnails = this.dialog.querySelector('.dialog-thumbnails-list-container');
      sidebar.append(title, this.viewerCounter);
      if (thumbnails) sidebar.append(thumbnails);
      this.dialog.append(sidebar);
    } else {
      this.viewerCounter = sidebar.querySelector('.otk-product-image-viewer__counter');
    }

    const viewer = this.dialog.querySelector('.dialog-zoomed-gallery');
    if (!viewer) return;

    const controls = document.createElement('div');
    controls.className = 'otk-product-image-viewer__controls';
    controls.innerHTML = `
      <button type="button" class="otk-product-image-viewer__arrow" data-otk-viewer-previous aria-label="Previous image">‹</button>
      <button type="button" class="otk-product-image-viewer__arrow" data-otk-viewer-next aria-label="Next image">›</button>
    `;
    this.dialog.append(controls);

    const { signal } = this.abortController;
    controls.querySelector('[data-otk-viewer-previous]')?.addEventListener('click', () => this.selectViewerMedia(-1), { signal });
    controls.querySelector('[data-otk-viewer-next]')?.addEventListener('click', () => this.selectViewerMedia(1), { signal });
    this.zoomDialog.addEventListener('zoom-media:selected', this.handleViewerMediaSelected, { signal });
    this.dialog.addEventListener('toggle', this.handleDialogToggle, { signal });
    this.dialog.addEventListener('keydown', this.handleViewerKeydown, { signal });
    this.dialog.addEventListener('close', this.handleDialogClose, { signal });
    this.dialog.addEventListener('click', this.preventViewerImageClose, { signal, capture: true });
    viewer.addEventListener('scroll', this.handleViewerScroll, { signal, passive: true });
  }

  captureOpener = (event) => {
    const slide = event.target instanceof Element ? event.target.closest('slideshow-slide.product-media-container--image') : null;
    if (!(slide instanceof HTMLElement)) return;

    slide.setAttribute('tabindex', '-1');
    this.opener = slide;
  };

  handlePointerEnter = (event) => {
    if (!desktopZoomQuery.matches || event.pointerType !== 'mouse') return;
    if (event.target instanceof Element && event.target.closest('slideshow-arrows')) return;

    this.pointer = { clientX: event.clientX, clientY: event.clientY };
    this.updateHoverZoom();
  };

  handlePointerMove = (event) => {
    if (!desktopZoomQuery.matches || event.pointerType !== 'mouse') return;
    if (event.target instanceof Element && event.target.closest('slideshow-arrows')) return;

    this.pointer = { clientX: event.clientX, clientY: event.clientY };
    if (this.animationFrame) return;
    this.animationFrame = window.requestAnimationFrame(() => {
      this.animationFrame = null;
      this.updateHoverZoom();
    });
  };

  getActiveImage() {
    const activeSlide = this.gallery.querySelector('slideshow-slide[aria-hidden="false"]');
    const image = activeSlide?.querySelector('img.product-media__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  updateHoverZoom() {
    const image = this.getActiveImage();
    if (!image || !this.pointer || !image.complete || !image.naturalWidth || !image.naturalHeight) {
      this.hideHoverZoom();
      return;
    }

    const imageRect = image.getBoundingClientRect();
    const renderedScale = Math.min(imageRect.width / image.naturalWidth, imageRect.height / image.naturalHeight);
    const renderedWidth = image.naturalWidth * renderedScale;
    const renderedHeight = image.naturalHeight * renderedScale;
    const renderedLeft = imageRect.left + (imageRect.width - renderedWidth) / 2;
    const renderedTop = imageRect.top + (imageRect.height - renderedHeight) / 2;
    const x = (this.pointer.clientX - renderedLeft) / renderedWidth;
    const y = (this.pointer.clientY - renderedTop) / renderedHeight;

    if (x < 0 || x > 1 || y < 0 || y > 1) {
      this.hideHoverZoom();
      return;
    }

    const zoom = 2.25;
    const paneWidth = imageRect.width;
    const paneHeight = imageRect.height;
    const backgroundWidth = renderedWidth * zoom;
    const backgroundHeight = renderedHeight * zoom;
    const backgroundX = Math.min(Math.max(x * backgroundWidth - paneWidth / 2, 0), Math.max(backgroundWidth - paneWidth, 0));
    const backgroundY = Math.min(Math.max(y * backgroundHeight - paneHeight / 2, 0), Math.max(backgroundHeight - paneHeight, 0));
    const lensWidth = renderedWidth / zoom;
    const lensHeight = renderedHeight / zoom;
    const lensX = Math.min(Math.max(x * renderedWidth - lensWidth / 2, 0), renderedWidth - lensWidth);
    const lensY = Math.min(Math.max(y * renderedHeight - lensHeight / 2, 0), renderedHeight - lensHeight);
    const galleryRect = this.gallery.getBoundingClientRect();
    const highResolutionUrl = image.getAttribute('data_max_resolution') || image.currentSrc || image.src;

    this.hoverPane.style.setProperty('--otk-product-hover-pane-height', `${paneHeight}px`);
    this.hoverPane.style.backgroundImage = `url("${highResolutionUrl.replace(/"/g, '\\"')}")`;
    this.hoverPane.style.backgroundSize = `${backgroundWidth}px ${backgroundHeight}px`;
    this.hoverPane.style.backgroundPosition = `-${backgroundX}px -${backgroundY}px`;
    this.hoverLens.style.width = `${lensWidth}px`;
    this.hoverLens.style.height = `${lensHeight}px`;
    this.hoverLens.style.left = `${imageRect.left - galleryRect.left + (imageRect.width - renderedWidth) / 2 + lensX}px`;
    this.hoverLens.style.top = `${imageRect.top - galleryRect.top + (imageRect.height - renderedHeight) / 2 + lensY}px`;
    this.hoverPane.hidden = false;
    this.hoverLens.hidden = false;
    this.gallery.classList.add('otk-product-gallery--zooming');
  }

  hideHoverZoom = () => {
    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.pointer = null;
    this.hoverPane?.setAttribute('hidden', '');
    this.hoverLens?.setAttribute('hidden', '');
    this.gallery.classList.remove('otk-product-gallery--zooming');
  };

  handleDialogToggle = () => {
    if (!this.dialog?.open) return;
    window.requestAnimationFrame(() => {
      const index = this.getViewerIndex();
      this.updateViewer(index);
      this.loadHighResolutionViewerImage(index);
      this.dialog.querySelector('.dialog-zoomed-gallery__close-button')?.focus({ preventScroll: true });
    });
  };

  handleDialogClose = () => {
    this.hideHoverZoom();
    if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
  };

  handleViewerMediaSelected = (event) => {
    this.updateViewer(event.detail?.index ?? this.getViewerIndex());
  };

  handleViewerKeydown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.selectViewerMedia(-1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.selectViewerMedia(1);
    }
  };

  preventViewerImageClose = (event) => {
    const imageMedia = event.target instanceof Element
      ? event.target.closest('.dialog-zoomed-gallery > .product-media-container--image')
      : null;
    if (imageMedia) event.stopPropagation();
  };

  handleViewerScroll = () => {
    if (this.animationFrame) return;
    this.animationFrame = window.requestAnimationFrame(() => {
      this.animationFrame = null;
      const viewer = this.dialog?.querySelector('.dialog-zoomed-gallery');
      if (!viewer) return;
      const index = Math.round(viewer.scrollLeft / Math.max(viewer.clientWidth, 1));
      this.updateViewer(index);
    });
  };

  getViewerIndex() {
    const selectedThumbnail = this.dialog?.querySelector('.dialog-thumbnails-list__thumbnail[aria-selected="true"]');
    const thumbnails = Array.from(this.dialog?.querySelectorAll('.dialog-thumbnails-list__thumbnail') || []);
    const index = thumbnails.indexOf(selectedThumbnail);
    return index < 0 ? 0 : index;
  }

  getViewerMedia() {
    return Array.from(this.dialog?.querySelectorAll('.dialog-zoomed-gallery > .product-media-container') || []);
  }

  selectViewerMedia(direction) {
    const media = this.getViewerMedia();
    if (media.length < 2) return;

    const index = (this.getViewerIndex() + direction + media.length) % media.length;
    this.zoomDialog?.handleThumbnailClick?.(index);
    this.updateViewer(index);
    this.loadHighResolutionViewerImage(index);
  }

  updateViewer(index) {
    const media = this.getViewerMedia();
    if (!media.length) return;

    const activeIndex = Math.min(Math.max(index, 0), media.length - 1);
    if (this.viewerCounter) this.viewerCounter.textContent = `${activeIndex + 1} / ${media.length}`;
  }

  loadHighResolutionViewerImage(index) {
    const media = this.getViewerMedia()[index];
    if (media) this.zoomDialog?.loadHighResolutionImage?.(media);
  }
}

const isOtkProductGallery = (gallery) =>
  gallery instanceof HTMLElement &&
  document.querySelector('.otk-product') &&
  gallery.closest('[data-testid="product-information"]');

const initializeOtkProductMediaViewers = (scope = document) => {
  const galleries = [];
  if (scope instanceof HTMLElement && scope.matches('media-gallery')) galleries.push(scope);
  galleries.push(...scope.querySelectorAll?.('media-gallery') || []);

  galleries.forEach((gallery) => {
    if (!isOtkProductGallery(gallery) || gallery.dataset.otkProductMediaViewerInitialized === 'true') return;
    new OtkProductMediaViewer(gallery).init();
  });
};

document.addEventListener('DOMContentLoaded', () => initializeOtkProductMediaViewers());
document.addEventListener('shopify:section:load', (event) => initializeOtkProductMediaViewers(event.target));

const galleryObserver = new MutationObserver((records) => {
  records.forEach((record) => {
    record.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) initializeOtkProductMediaViewers(node);
    });
  });
});

galleryObserver.observe(document.documentElement, { childList: true, subtree: true });
